import React, { useState } from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import moment from "moment"
import { IPersonnelArretMaladieModel } from "@common-models/*"
import { CONST_CONSULTANT_ARRET_MALADIE_TYPE_TYPE_DEMI_APREM, CONST_CONSULTANT_ARRET_MALADIE_TYPE_TYPE_DEMI_MATIN } from "@common-constants/*"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import GlobalHelper from "@common-helpers/GlobalHelper"
import useDateHelper from "@common-helpers/useDateHelper"
import MyGoogleDriveFileViewerInModal from "../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { getPersonnelMaladiesRequest } from "../../core/_requests"
import ActiviteCard from "./_ActiviteCard"

const REACT_QUERY_KEY = "REACT_QUERY_KEY_GET_ACTIVITE_MALADIES"

// Section « Arrêts maladie » (consultation seule) de l'onglet Activité. Triée par date de début décroissante
// (ordre garanti côté API), avec date de création affichée et justificatif consultable.
const MaladieSection = ({ personnelId }: { personnelId: number }) => {
     const { countWeekdaysBetweenDates } = useDateHelper()
     const [justificatifInModal, setJustificatifInModal] = useState<IPersonnelArretMaladieModel>()

     const query = useQuery<IPersonnelArretMaladieModel[], AxiosError>(REACT_QUERY_KEY, () => getPersonnelMaladiesRequest(personnelId, {}).then(r => r.data))

     function getDureeLabel(item: IPersonnelArretMaladieModel): string {
          const isDemi = item.type === CONST_CONSULTANT_ARRET_MALADIE_TYPE_TYPE_DEMI_MATIN || item.type === CONST_CONSULTANT_ARRET_MALADIE_TYPE_TYPE_DEMI_APREM
          const duree = isDemi ? 0.5 : countWeekdaysBetweenDates(item.du as moment.Moment, item.au as moment.Moment)
          return `${duree} ${duree > 1 ? "jours" : "jour"}`
     }

     function getPeriode(item: IPersonnelArretMaladieModel) {
          const isDemi = item.type === CONST_CONSULTANT_ARRET_MALADIE_TYPE_TYPE_DEMI_MATIN || item.type === CONST_CONSULTANT_ARRET_MALADIE_TYPE_TYPE_DEMI_APREM
          if (isDemi) {
               return {
                    label: `${item.du?.parseZone().format("DD/MM/YYYY")} — ${item.typeLabel}`,
                    tooltip: (
                         <span>
                              Le {GlobalHelper.firstLetterUppercase(item.du?.parseZone().format("dddd D MMMM YYYY"))} ({item.typeLabel})
                         </span>
                    ),
               }
          }
          return {
               label: `${item.du?.parseZone().format("DD/MM/YYYY")} → ${item.au?.parseZone().format("DD/MM/YYYY")}`,
               tooltip: (
                    <span>
                         Du {GlobalHelper.firstLetterUppercase(item.du?.parseZone().format("dddd D MMMM YYYY"))} au{" "}
                         {GlobalHelper.firstLetterUppercase(item.au?.parseZone().format("dddd D MMMM YYYY"))}
                    </span>
               ),
          }
     }

     if (query.isFetching)
          return (
               <div className={"text-center py-4"}>
                    <MySimpleSpinner size={"xl"} />
               </div>
          )

     if (query.isError)
          return (
               <MyAlert type={"danger"}>
                    <div className={"d-flex align-items-center"}>
                         <div className={"me-2"}>{query.error?.response?.data?.detail}</div>
                         <button className={"btn btn-sm btn-danger p-0 px-3 py-2"} onClick={() => query.refetch()}>
                              Recharger
                         </button>
                    </div>
               </MyAlert>
          )

     if (!query.data || query.data.length === 0) return <MyAlert type={"primary"}>Aucun arrêt maladie n'a été déclaré.</MyAlert>

     return (
          <>
               <div className={"d-flex flex-column gap-3"}>
                    {query.data.map((item, key) => {
                         const periode = getPeriode(item)
                         return (
                              <ActiviteCard
                                   key={key}
                                   color={"danger"}
                                   icon={"fa-file-medical"}
                                   title={item.motifLabel || "Arrêt maladie"}
                                   periodeLabel={periode.label}
                                   periodeTooltip={periode.tooltip}
                                   dureeLabel={getDureeLabel(item)}
                                   createdAtLabel={item.createdAt?.parseZone().format("DD/MM/YYYY")}
                                   onViewJustificatif={item.justificatifGoogleDriveID ? () => setJustificatifInModal(item) : undefined}
                              />
                         )
                    })}
               </div>

               {justificatifInModal && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={justificatifInModal.justificatifGoogleDriveID as string}
                         googleDriveMimetype={justificatifInModal.justificatifGoogleDriveMimeType as string}
                         modalTitle={<>Justificatif</>}
                         handleCloseModal={() => setJustificatifInModal(undefined)}
                         modalSize={"xl"}
                    />
               )}
          </>
     )
}

export default MaladieSection
